import { ViewController } from '@estee/elc-service';
import { action, computed, observable } from 'mobx';
import { StoresRepository } from '~repositories/StoresRepository';
import { FindInStore } from '~domain/entities/FindInStore';
import { StoreItemSelectedUseCase } from '~use-cases/StoreItemSelectedUseCase';
import { StoreItemDeselectedUseCase } from '~use-cases/StoreItemDeselectedUseCase';
import { StoreInfoClickedUseCase } from '~use-cases/StoreInfoClickedUseCase';
import { StorePhoneClickedUseCase } from '~use-cases/StorePhoneClickedUseCase';
import { Config } from '~domain/entities/Config';
import { GoToDirectoryClickedUseCase } from '~use-cases/GoToDirectoryClickedUseCase';
import { LocationResetClickedUseCase } from '~use-cases/LocationResetClickedUseCase';
import { StoresViewController } from './StoresViewController';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/StoresService';
import { UserLocationRepository } from '~repositories/UserLocationRepository';
import { StoresMapViewController } from './StoresMapViewController';
import { events, emit } from '@estee/elc-service-bus';
import { StoreDetailsViewController } from './StoreDetailsViewController';

export class StoresSidebarViewController extends ViewController {
    @observable private storesRepository: StoresRepository;
    @observable private userLocationRepository: UserLocationRepository;
    @observable private findInStore: FindInStore;
    @observable private storesViewController: StoresViewController;
    @observable private storesMapViewController: StoresMapViewController;
    @observable private storeDetailsViewController: StoreDetailsViewController;
    private configContainer: Config;

    constructor(
        storesRepository: StoresRepository,
        findInStore: FindInStore,
        config: Config,
        userLocationRepository: UserLocationRepository
    ) {
        super();
        this.storesRepository = storesRepository;
        this.userLocationRepository = userLocationRepository;
        this.findInStore = findInStore;
        this.configContainer = config;
        this.storesViewController = diContainer.get(serviceNames.storesViewController);
        this.storesMapViewController = diContainer.get(serviceNames.storesMapViewController);
        this.storeDetailsViewController = diContainer.get(serviceNames.storeDetailsViewController);
    }

    @action
    public loaded = async () => {
        this.isLoading = false;
    };

    @computed
    get currentStore() {
        return this.stores[0];
    }

    @computed
    get stores() {
        return this.storesRepository.stores;
    }

    @computed
    get filtersEnabled() {
        if (this.configContainer.config.featureFlags) {
            return this.configContainer.config.featureFlags.enableFilters;
        }

        return false;
    }

    @action
    public onPhoneClick = () => {
        const { id, name } = this.currentStore;
        new StorePhoneClickedUseCase(id, name).execute();
    };

    @action
    public onGoToDirectoryClick = () => {
        new GoToDirectoryClickedUseCase().execute();
    };

    @action
    public onStoreInfoLinkClick = () => {
        const { id, name } = this.currentStore;
        emit(events.STORE_DETAILS_ADDRESS_LINK_CLICKED, {
            eventName: 'store_finder',
            eventCategory: 'store_finder_address_link_click',
            eventAction: `${name}`,
            eventLabel: `${name}`
        });
        new StoreInfoClickedUseCase(id, name).execute();
    };

    @action
    public onStoreSelected =
        (nextButtonRef?: React.RefObject<HTMLDivElement>) =>
        async (storeId: string, counterId?: string, isSelecting?: boolean, storeName?: string) => {
            if (counterId) {
                const { updateSelectedStore } = this.storesViewController;
                updateSelectedStore(counterId, isSelecting);

                if (nextButtonRef && nextButtonRef.current) {
                    nextButtonRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'start'
                    });
                }
            }
            const { toggleInfoWindow } = this.storesMapViewController;
            toggleInfoWindow(false);

            if (this.storesRepository.selectedStoreId !== storeId) {
                emit(events.STORE_SELECTED, {
                    eventName: 'store_finder',
                    eventCategory: 'store_finder_store_selection',
                    eventAction: `${storeName}`,
                    eventLabel: 'store_selected'
                });
            }

            if (this.storeDetailsViewController) {
                const { onExpandedStoreHoursIdChange } = this.storeDetailsViewController;
                onExpandedStoreHoursIdChange();
            }

            await new StoreItemSelectedUseCase(storeId).execute();
        };

    @action
    public onStoreDeselected = (counterId?: string, isSelecting?: boolean) => {
        new StoreItemDeselectedUseCase().execute();

        const { updateSelectedStore } = this.storesViewController;
        updateSelectedStore(counterId, isSelecting);
    };

    public onLocationReset = () => {
        new LocationResetClickedUseCase().execute();
    };

    public getGeolocationStatus = async (): Promise<PermissionState> => {
        const status = await this.userLocationRepository.fetchGeolocationPermissions();

        return status;
    };

    @computed
    public get data() {
        return {
            stores: this.stores,
            storeInventoryMap: this.storesRepository.storeInventory,
            findInStore: this.findInStore,
            areStoresLoading: this.storesRepository.isLoading,
            selectedStoreId: this.storesRepository.selectedStoreId,
            apiKey: this.configContainer.config.googleApiKey,
            mapRegion: this.configContainer.config.mapRegion,
            mapLanguage: this.configContainer.config.mapLanguage,
            filtersEnabled: this.filtersEnabled,
            geolocationPermissions: this.getGeolocationStatus
        };
    }
}
